import React, { useEffect, useRef, useState } from "react";
import { ChatDisclaimer } from "@/components/AiNotice";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Languages, Send, Loader2, Trash2, Lightbulb, Sparkles } from "lucide-react";
import client from "@/lib/api";
import { useLang } from "@/lib/lang";
import VoiceButton from "@/components/VoiceButton";

const SESSION_KEY = "lechatceltique.polyglotte_session";
const NATIVE_KEY = "lechatceltique.polyglotte_native";
const TARGET_KEY = "lechatceltique.polyglotte_target";
const MODE_KEY = "lechatceltique.polyglotte_mode";

// 20 native languages (user's mother tongue)
const NATIVE_LANGS = [
  { code: "fr", label: "Français" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "pt", label: "Português" },
  { code: "it", label: "Italiano" },
  { code: "de", label: "Deutsch" },
  { code: "nl", label: "Nederlands" },
  { code: "pl", label: "Polski" },
  { code: "ro", label: "Română" },
  { code: "tr", label: "Türkçe" },
  { code: "sv", label: "Svenska" },
  { code: "ru-lat", label: "Russkiy (translit.)" },
  { code: "ar-rumi", label: "ʿArabī (rumi)" },
  { code: "fa-lat", label: "Fārsī (translit.)" },
  { code: "hi-lat", label: "Hindī (romanisé)" },
  { code: "zh-pinyin", label: "Zhōngwén (pinyin)" },
  { code: "ja-romaji", label: "Nihongo (romaji)" },
  { code: "ko-rr", label: "Hangugeo (RR)" },
  { code: "sw", label: "Kiswahili" },
  { code: "id", label: "Bahasa Indonesia" },
];

// 30 target learning languages (all in Latin script)
const TARGET_LANGS = [
  "Français", "English", "Español", "Português", "Italiano",
  "Deutsch", "Nederlands", "Svenska", "Polski", "Română",
  "Türkçe", "Kiswahili", "Bahasa Indonesia", "Tiếng Việt",
  "Tagalog", "ʻŌlelo Hawaiʻi", "Zhōngwén (pinyin)", "Nihongo (romaji)",
  "Hangugeo (RR)", "Hindī (romanisé)", "Bāṅlā (romanisé)",
  "ʿArabī (rumi)", "Fārsī (translit.)", "ʿIvrit (romanisé)",
  "Russkiy (translit.)", "Elliniká (romanisé)", "Suomi", "Eesti",
  "Euskara", "Te Reo Māori",
];

export default function Polyglotte() {
  const { lang: uiLang, setLang } = useLang();
  const [native, setNative] = useState(() => localStorage.getItem(NATIVE_KEY) || "fr");
  const [target, setTarget] = useState(() => localStorage.getItem(TARGET_KEY) || "Español");
  const [mode, setMode] = useState(() => localStorage.getItem(MODE_KEY) || "conversation"); // "conversation" | "philo"
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || "");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => { localStorage.setItem(NATIVE_KEY, native); }, [native]);
  useEffect(() => { localStorage.setItem(TARGET_KEY, target); }, [target]);
  useEffect(() => { localStorage.setItem(MODE_KEY, mode); }, [mode]);

  useEffect(() => {
    if (!sessionId) return;
    client.get("/chat/polyglotte/history", { params: { session_id: sessionId } })
      .then((r) => setMessages(r.data?.messages || []))
      .catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text) return;
    const nativeLabel = NATIVE_LANGS.find((l) => l.code === native)?.label || native;
    const framed =
      mode === "conversation"
        ? `[Langue maternelle : ${nativeLabel}] [Langue cible d'apprentissage : ${target}] Question/sujet : ${text}`
        : `[Langue maternelle : ${nativeLabel}] [Langue cible : ${target}] [Mode : OUTIL PHILOSOPHIQUE court — stoïcisme / libre-arbitre / anti-addiction] Sujet : ${text}`;

    setMessages((m) => [...m, { sender: "user", message: text, created_at: new Date().toISOString() }]);
    setInput("");
    setLoading(true);
    try {
      const r = await client.post("/chat/polyglotte", {
        message: framed,
        session_id: sessionId || null,
        lang: uiLang,
      });
      const data = r.data;
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem(SESSION_KEY, data.session_id);
      }
      setMessages((m) => [...m, { sender: "advisor", message: data.reply, created_at: new Date().toISOString() }]);
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error(uiLang === "fr" ? "Pause — reviens dans une minute." : "Pause — retry in a minute.");
      } else {
        toast.error(uiLang === "fr" ? "Le Polyglotte reprend son souffle." : "The Polyglot pauses.");
      }
    } finally {
      setLoading(false);
    }
  };

  const wipe = async () => {
    try { await client.post("/chat/polyglotte/clear", { session_id: sessionId || null }); } catch { /* ignore */ }
    localStorage.removeItem(SESSION_KEY);
    setSessionId("");
    setMessages([]);
    toast.success(uiLang === "fr" ? "Session effacée." : "Session wiped.");
  };

  return (
    <div data-testid="polyglotte-page" className="min-h-screen relative"
         style={{
           background: "radial-gradient(ellipse at top, #0a2436 0%, #061625 55%, #020810 100%)",
           color: "#dceaf5",
           overflowX: "hidden",
         }}>
      <header className="relative z-30">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-6 flex items-center justify-between flex-wrap gap-3">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-[0.24em]" style={{ color: "#5fb3d9" }}>
            <ArrowLeft size={14} /> {uiLang === "fr" ? "Retour" : "Back"}
          </Link>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center rounded-full text-[11px] font-mono overflow-hidden"
                 style={{ background: "rgba(20,40,60,0.4)", border: "1px solid rgba(95,179,217,0.3)" }}>
              <button onClick={() => setLang("fr")} type="button" data-testid="polyglotte-lang-fr" className="px-2.5 py-1.5"
                      style={{ background: uiLang === "fr" ? "#5fb3d9" : "transparent", color: uiLang === "fr" ? "#031018" : "#dceaf5", fontWeight: uiLang === "fr" ? 600 : 400 }}>FR</button>
              <button onClick={() => setLang("en")} type="button" data-testid="polyglotte-lang-en" className="px-2.5 py-1.5"
                      style={{ background: uiLang === "en" ? "#5fb3d9" : "transparent", color: uiLang === "en" ? "#031018" : "#dceaf5", fontWeight: uiLang === "en" ? 600 : 400 }}>EN</button>
            </div>
            <button type="button" onClick={wipe} data-testid="polyglotte-wipe"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-mono"
                    style={{ background: "rgba(60,30,30,0.3)", border: "1px solid rgba(220,100,100,0.3)", color: "#e8a8a8" }}>
              <Trash2 size={11} /> {uiLang === "fr" ? "Effacer" : "Wipe"}
            </button>
          </div>
        </div>
      </header>

      <section className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 pt-4 pb-6">
        <div className="overline mb-3" style={{ color: "#5fb3d9" }}>
          <span className="kicker-line" />
          Polyglotte · {uiLang === "fr" ? "30 langues · gratuit illimité" : "30 languages · free unlimited"}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02]" style={{ color: "#f0f8ff" }}>
          {uiLang === "fr" ? (
            <><span className="italic" style={{ color: "#5fb3d9" }}>Le Polyglotte</span>, Grammairien 2.0 universel.</>
          ) : (
            <><span className="italic" style={{ color: "#5fb3d9" }}>The Polyglot</span>, universal Grammarian 2.0.</>
          )}
        </h1>
        <p className="mt-4 max-w-3xl text-base" style={{ color: "#a8c8dc" }}>
          {uiLang === "fr"
            ? "Pose ta question générale sur la vie. Je te réponds dans la langue cible (200+ mots), avec les voix du poète, de la grammairienne et de l'enfant, puis un tutorat motivant dans ta langue maternelle. Toutes les langues sont transcrites en alphabet latin pour que tu puisses lire sans alphabet nouveau."
            : "Ask a general life question. I reply in the target language (200+ words), with voices of the poet, the grammarian, and the child, then a motivating tutoring section in your mother tongue. All languages written in Latin script so you can read without a new alphabet."}
        </p>
      </section>

      <section className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 pb-6">
        <div className="grid gap-3 sm:grid-cols-3 p-4 rounded-2xl"
             style={{ background: "rgba(10,28,45,0.6)", border: "1px solid rgba(95,179,217,0.25)" }}>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-[0.2em] mb-1" style={{ color: "#5fb3d9" }}>
              {uiLang === "fr" ? "🏠 Langue maternelle" : "🏠 Mother tongue"}
            </label>
            <select value={native} onChange={(e) => setNative(e.target.value)} data-testid="polyglotte-native"
                    style={{
                      width: "100%", padding: "9px 12px", borderRadius: 10,
                      background: "rgba(0,0,0,0.4)", border: "1px solid rgba(95,179,217,0.35)",
                      color: "#f0f8ff", fontSize: 13,
                    }}>
              {NATIVE_LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-[0.2em] mb-1" style={{ color: "#5fb3d9" }}>
              {uiLang === "fr" ? "🎯 Langue d'apprentissage" : "🎯 Learning language"}
            </label>
            <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="polyglotte-target"
                    style={{
                      width: "100%", padding: "9px 12px", borderRadius: 10,
                      background: "rgba(0,0,0,0.4)", border: "1px solid rgba(95,179,217,0.35)",
                      color: "#f0f8ff", fontSize: 13,
                    }}>
              {TARGET_LANGS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-[0.2em] mb-1" style={{ color: "#5fb3d9" }}>
              {uiLang === "fr" ? "🧭 Mode" : "🧭 Mode"}
            </label>
            <div className="flex gap-1.5">
              <button type="button" onClick={() => setMode("conversation")} data-testid="polyglotte-mode-conv"
                      style={{
                        flex: 1, padding: "9px 10px", borderRadius: 10,
                        background: mode === "conversation" ? "#5fb3d9" : "rgba(0,0,0,0.4)",
                        color: mode === "conversation" ? "#031018" : "#dceaf5",
                        border: `1px solid ${mode === "conversation" ? "#5fb3d9" : "rgba(95,179,217,0.35)"}`,
                        fontSize: 11, fontFamily: "'IBM Plex Mono', monospace",
                        letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600,
                        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4,
                      }}>
                <Languages size={11} /> {uiLang === "fr" ? "Conversation" : "Chat"}
              </button>
              <button type="button" onClick={() => setMode("philo")} data-testid="polyglotte-mode-philo"
                      style={{
                        flex: 1, padding: "9px 10px", borderRadius: 10,
                        background: mode === "philo" ? "#f0b84a" : "rgba(0,0,0,0.4)",
                        color: mode === "philo" ? "#1a1004" : "#dceaf5",
                        border: `1px solid ${mode === "philo" ? "#f0b84a" : "rgba(240,184,74,0.35)"}`,
                        fontSize: 11, fontFamily: "'IBM Plex Mono', monospace",
                        letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600,
                        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4,
                      }}>
                <Lightbulb size={11} /> {uiLang === "fr" ? "Philo" : "Philo"}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 pb-24">
        <div ref={boxRef} className="sacred-chat"
             style={{
               height: "60vh", minHeight: 380, overflowY: "auto",
               background: "rgba(5,15,25,0.6)",
               border: "1px solid rgba(95,179,217,0.25)",
               borderRadius: 16, padding: "18px 20px",
             }}>
          {messages.length === 0 && !loading && (
            <div className="text-center opacity-70 py-10" style={{ color: "#8ab0cc" }}>
              <Sparkles size={28} className="mx-auto mb-3" />
              <p className="text-sm">
                {uiLang === "fr"
                  ? "Choisis tes langues, ton mode, puis pose ta question."
                  : "Pick your languages, mode, then ask."}
              </p>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} style={{
              display: "flex", margin: "10px 0",
              justifyContent: m.sender === "user" ? "flex-end" : "flex-start",
            }}>
              <div style={{
                maxWidth: m.sender === "user" ? "78%" : "92%",
                padding: "14px 18px", borderRadius: 16,
                background: m.sender === "user"
                  ? "linear-gradient(135deg, #5fb3d9, #4a8cb0)"
                  : "rgba(255,255,255,0.03)",
                color: m.sender === "user" ? "#031018" : "#dceaf5",
                border: m.sender === "user" ? "none" : "1px solid rgba(95,179,217,0.2)",
                lineHeight: 1.65, fontSize: 14.5,
                borderBottomRightRadius: m.sender === "user" ? 4 : 16,
                borderBottomLeftRadius: m.sender === "user" ? 16 : 4,
              }}>
                {m.sender === "advisor" ? <ReactMarkdown>{m.message}</ReactMarkdown> : <span>{m.message}</span>}
                {m.sender === "advisor" && (
                  <div className="mt-2">
                    <VoiceButton text={m.message} lang={uiLang} accent="#5fb3d9" testId={`polyglotte-voice-${i}`} />
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div style={{ display: "flex", margin: "10px 0" }}>
              <div style={{
                padding: "12px 16px", borderRadius: 16,
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(95,179,217,0.3)",
                color: "#dceaf5", display: "inline-flex", gap: 8, alignItems: "center",
              }}>
                <Loader2 size={14} className="animate-spin" />
                <span className="text-sm">
                  {uiLang === "fr" ? `Le Polyglotte compose en ${target}…` : `The Polyglot composes in ${target}…`}
                </span>
              </div>
            </div>
          )}
        </div>

        <form className="mt-4 flex flex-col sm:flex-row gap-2"
              onSubmit={(e) => { e.preventDefault(); send(); }}>
          <textarea
            data-testid="polyglotte-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={uiLang === "fr" ? "Pose ta question sur la vie…" : "Ask your life question…"}
            rows={2}
            style={{
              flex: 1, resize: "none", padding: "12px 16px", borderRadius: 14,
              background: "rgba(0,0,0,0.4)", border: "1px solid rgba(95,179,217,0.3)",
              color: "#f0f8ff", fontSize: 15, outline: "none",
            }}
            disabled={loading}
          />
          <button type="submit" disabled={loading || !input.trim()}
                  data-testid="polyglotte-send"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full"
                  style={{
                    background: "linear-gradient(135deg, #5fb3d9, #4a8cb0)",
                    color: "#031018", fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase",
                    opacity: (loading || !input.trim()) ? 0.4 : 1,
                  }}>
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {uiLang === "fr" ? "Envoyer" : "Send"}
          </button>
        </form>
        <ChatDisclaimer testId="polyglotte-ai-disclaimer" />
      </section>
    </div>
  );
}
